import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ActivityIndicator, TextInput, KeyboardAvoidingView, Platform, Alert } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { addDraft, getDrafts, removeDraft, updateDraft, DraftItem } from '../utils/saved';
import { createPost } from '../services/bluesky';
import { Theme, useTheme, useThemedStyles } from '../theme';

interface Props {
  onBack: () => void;
}

const Drafts: React.FC<Props> = ({ onBack }) => {
  const insets = useSafeAreaInsets();
  const [drafts, setDrafts] = useState<DraftItem[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const load = async () => {
    setLoading(true);
    const data = await getDrafts();
    setDrafts(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    if (!text.trim()) return;
    await addDraft(text.trim());
    setText('');
    load();
  };

  const handleDelete = async (id: string) => {
    await removeDraft(id);
    load();
  };

  const handleSend = async (draft: DraftItem) => {
    try {
      setSendingId(draft.id);
      await createPost({ text: draft.text });
      await removeDraft(draft.id);
      load();
      Alert.alert(t('drafts.sent'));
    } catch (e) {
      console.error('Failed to send draft', e);
      Alert.alert(t('common.error'), t('drafts.sendFailed'));
    } finally {
      setSendingId(null);
    }
  };

  const renderItem = ({ item }: { item: DraftItem }) => (
    <View style={styles.row}>
      <View style={{ flex: 1 }}>
        <Text style={styles.text} numberOfLines={3}>{item.text}</Text>
        <Text style={styles.sub}>{new Date(item.createdAt).toLocaleString()}</Text>
      </View>
      <TouchableOpacity style={styles.iconBtn} onPress={() => setText(item.text)}>
        <Ionicons name="create-outline" size={20} color={theme.colors.text} />
      </TouchableOpacity>
      <TouchableOpacity style={styles.iconBtn} onPress={() => handleSend(item)} disabled={sendingId === item.id}>
        {sendingId === item.id ? (
          <ActivityIndicator size="small" color={theme.colors.primary} />
        ) : (
          <Ionicons name="send-outline" size={20} color={theme.colors.primary} />
        )}
      </TouchableOpacity>
      <TouchableOpacity style={styles.iconBtn} onPress={() => handleDelete(item.id)}>
        <Ionicons name="trash-outline" size={20} color={theme.colors.danger} />
      </TouchableOpacity>
    </View>
  );

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <View style={[styles.header, { paddingTop: insets.top, height: 56 + insets.top }]}>
        <TouchableOpacity onPress={onBack} style={styles.backBtn}>
          <Ionicons name="chevron-back" size={24} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={styles.title}>{t('drafts.title')}</Text>
      </View>
      <View style={[styles.container, { paddingBottom: insets.bottom }]}>
        <View style={styles.inputRow}>
          <TextInput
            style={styles.input}
            placeholder={t('drafts.inputPlaceholder')}
            multiline
            value={text}
            onChangeText={setText}
            placeholderTextColor={theme.colors.textMuted}
          />
          <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={!text.trim()}>
            <Text style={styles.saveText}>保存</Text>
          </TouchableOpacity>
        </View>

        {loading ? (
          <View style={styles.center}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
          </View>
        ) : (
          <FlatList
            data={drafts}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            ListEmptyComponent={
              <View style={styles.center}>
                <Text style={styles.empty}>下書きはまだありません</Text>
              </View>
            }
          />
        )}
      </View>
    </KeyboardAvoidingView>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  header: {
    height: 56,
    backgroundColor: theme.colors.card,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  backBtn: { padding: 4, marginRight: 8 },
  title: { color: theme.colors.text, fontSize: 18, fontWeight: '600' },
  container: { flex: 1, backgroundColor: theme.colors.background },
  inputRow: {
    flexDirection: 'row',
    padding: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  input: {
    flex: 1,
    minHeight: 60,
    borderWidth: 0.5,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 10,
    textAlignVertical: 'top',
    backgroundColor: theme.colors.input,
    color: theme.colors.text,
  },
  saveBtn: {
    marginLeft: 8,
    alignSelf: 'flex-start',
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
  },
  saveText: { color: theme.colors.primaryText, fontWeight: '600' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  text: { fontSize: 14, color: theme.colors.text },
  sub: { fontSize: 11, color: theme.colors.textMuted, marginTop: 4 },
  iconBtn: { padding: 6 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20, backgroundColor: theme.colors.background },
  empty: { color: theme.colors.textMuted, fontSize: 13 },
});

export default Drafts;
